<template>
  <section class="ma-4 ma-md-8">
    <t-page-title v-if="can('create', 'events')" title="Eventos" :on-add="() => $router.push({ name: 'create-event' })" />
    <t-page-title v-else title="Eventos" />
    <v-row justify="center" align="center">
      <v-col>
        <data-table
          ref="datatable"
          :headers="headers"
          :provider="fetch"
          :filters="filters"
          :resource="resource"
          :actions="actions"
          :metadata="metadata"
        >
          <template #[`item.id`]="{ item }">
            <span> {{ item.id.split('-')[0] }}</span>
          </template>
          <template #[`item.dates`]="{ item }">
            <span> {{ $luxon.DateTime.fromSQL(item.dates.start).toFormat('dd/MM/yyyy HH:mm:ss') }}</span>
          </template>
          <template #[`item.status`]="{ item }">
            <v-btn
              icon="mdi-account"
              size="x-small"
              variant="flat"
              class="cursor-default"
              :class="getStatusColor(item)"
            />
          </template>
          <template #[`item.readings_count`]="{ item }">
            <span> {{ item.readings_count }} </span>
          </template>
          <template #[`custom.actions`]="{ item }">
            <v-tooltip v-if="item.dates.sales.open && DateTime.fromSQL(item.dates.sales.close) > DateTime.now()" text="Visualizar" location="top">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  density="compact"
                  min-height="32"
                  min-width="32"
                  elevation="1"
                  class="mr-2 pa-2"
                  icon
                  @click="preview(item)"
                >
                  <v-icon size="x-small" icon="fas fa-eye" color="primary" />
                </v-btn>
              </template>
            </v-tooltip>
            <v-tooltip v-if="can('read', 'sales.report.pos')" text="Faturamento" location="top">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  density="compact"
                  min-height="32"
                  min-width="32"
                  elevation="1"
                  class="mr-2 pa-2"
                  icon
                  @click="router.push({ name: 'event-revenue-report', params: { event: item.id, } })"
                >
                  <v-icon size="x-small" icon="fas fa-sack-dollar" color="primary" />
                </v-btn>
              </template>
            </v-tooltip>
            <v-tooltip v-if="can('read', 'readings.overview')" text="Borderô" location="top">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  density="compact"
                  min-height="32"
                  min-width="32"
                  elevation="1"
                  class="mr-2 pa-2"
                  icon
                  @click="router.push({ name: 'overview-events', params: { event: item.id, } })"
                >
                  <v-icon size="x-small" icon="fas fa-qrcode" color="primary" />
                </v-btn>
              </template>
            </v-tooltip>
            <v-tooltip v-if="can('update', 'events')" text="Lugares" location="top">
              <template #activator="{ props }">
                <v-btn
                  v-bind="props"
                  density="compact"
                  min-height="32"
                  min-width="32"
                  elevation="1"
                  class="mr-2 pa-2"
                  icon
                  @click="router.push({ name: 'event-seats', params: { event: item.id, } })"
                >
                  <v-icon size="x-small" icon="fa-solid fa-chair" color="primary" />
                </v-btn>
              </template>
            </v-tooltip>
          </template>
        </data-table>
      </v-col>
    </v-row>
  </section>
</template>

<script lang="ts" setup>

import { computed, ref, Ref, useTemplateRef } from 'vue'
import TPageTitle from './../../components/TPageTitle.vue'
import type { DataTableFilter, DataTableHeader, DataTableMeta, DataTableMetaItem, DefaultDataTableActions } from '../../types/DataTable'
import ProducerService from '@/services/ProducerService'
import { Resources } from '@/plugins/permissions/typing'
import { useTextFilter } from '@/helpers/reports/textFilter'
import { useSnackBar } from '@/stores/snackbar'
import DataTable from '@/components/datatable/DataTable.vue'
import { useRouter } from 'vue-router'
import { useDateIntervalFilter } from '@/helpers/reports/dateIntervalFilter'
import Event from '@/types/Event'
import { DateTime } from 'luxon'
import { useProducerStore } from '@/stores/producer'
import { useAbility } from '@casl/vue'
import { useLoader } from '@/stores/loader'
import EventService from '@/services/EventService'
import { AxiosError } from 'axios'
import { useDialog } from '@/stores/dialog'

const datatable = useTemplateRef('datatable')

const dialog = useDialog()
const snackbar = useSnackBar()
const router = useRouter()
const producer = useProducerStore().producer
const loader = useLoader()
const { can } = useAbility()

const resource: Resources = 'events'

const headers: Ref<DataTableHeader[]> = ref([
  { title: 'Status', value: 'status', key: 'status', sortable: false, align: 'center' },
  { title: 'ID', value: 'id', key: 'id', sortable: false, align: 'center' },
  { title: 'Evento', value: 'title', key: 'title', sortable: false },
  { title: 'Data', value: 'dates', key: 'dates', sortable: false, align: 'center' },
  { title: 'Configuração', value: 'configuration.name', key: 'configuration.name', sortable: false, align: 'center' },
  { title: 'Local', value: 'configuration.mapping.local.name', key: 'configuration.mapping.local.name', sortable: false, align: 'center' },
  { title: 'Ingressos', value: 'passports_count', key: 'passports_count', sortable: false, align: 'center' },
  { title: 'Acessos', value: 'readings_count', key: 'readings_count', sortable: false, align: 'center' },
  { title: 'Ações', value: 'actions', key: 'actions', sortable: false, align: 'center' },
])

const filters: Ref<DataTableFilter[]> = ref([
  useTextFilter({ label: 'Nome', field: 'name', required: false }),
  useDateIntervalFilter({ label: 'Data', field: 'end_between', required: false, initial: null }),
])

const metadata: DataTableMeta = {
  headers: [
    {
      title: 'Eventos',
      value: 'count',
      key: 'count',
      align: 'center',
      sortable: false,
    },
    {
      title: 'Ingressos',
      value: 'tickets',
      key: 'tickets',
      align: 'center',
      sortable: false,
    },
    {
      title: 'Acessos',
      value: 'readings',
      key: 'readings',
      align: 'center',
      sortable: false,
    },
  ],
  items: [] as DataTableMetaItem[],
}

const actions = computed((): DefaultDataTableActions => ({
  update: {
    callback: (item: Event) => router.push({ name: 'edit-event', params: { id: item.id } }),
    condition: () => can('update', 'events'),
    icon: 'fa-solid fa-pen',
    color: 'info',
  },
  destroy: {
    callback: (item: Event) => destroy(item),
    condition: () => can('delete', 'events'),
    icon: 'fa-solid fa-trash',
    color: 'error',
  },
}))

function getStatusColor(event: Event) {
  const now = DateTime.now()
  if (DateTime.fromSQL(event.dates.end) < now) {
    return 'bg-grey-lighten-1'
  }

  if (
    DateTime.fromSQL(event.dates.sales.open) < now
    && DateTime.fromSQL(event.dates.sales.close) > now
  ) {
    return 'bg-green-lighten-1'
  }

  return 'bg-primary'
}

function preview(event: Event) {
  const domain = producer?.settings?.domain ?? 'ingressodevantagens.com.br'
  // Requested by Vitor, the `show_tickets_selector` forces the ticket-selector to be shown despite the event sales
  // not being open.
  window.open(`https://${domain}/ticket/buy/${event.id}?show_tickets_selector`)
}

async function fetch(page: number, filters: string, per_page?: number) {
  try {
    const response = await (new ProducerService()).events(page, filters, per_page)

    metadata.items = [
      {
        count: response.data.additional!.count,
        tickets: response.data.additional!.tickets,
        readings: response.data.additional!.readings,
      },
    ]

    return response
  } catch (error) {
    snackbar.show(
      'Não foi possível buscar os eventos',
      'error',
    )

    throw error
  }
}

async function destroy(item: Event) {
  const confirm = await dialog.open({
    title: 'Confirmação de exclusão',
    message: 'Realmente deseja excluir este evento? Essa ação é irreversível.',
  })

  if (!confirm) return

  try {
    loader.increment()
    await (new EventService()).destroy(item)

    snackbar.show(
      'Evento excluído com sucesso.',
      'success',
    )

    if (datatable.value !== null) {
      datatable.value.fetch(1)
    }
  } catch (error) {
    if (error instanceof AxiosError) {
      snackbar.show(error.response?.data?.message, 'error')
    } else if (error instanceof Error) {
      snackbar.show(error.message, 'error')
    } else {
      snackbar.show('Não foi possível atualizar as informações do evento', 'error')
    }
  } finally {
    loader.decrement()
  }
}
</script>
